import React, { Fragment, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardBody, CardHeader, Col, Container, Row, Button, Alert, Table } from 'reactstrap';
import { UploadCloud, X, FileText } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import API_BASE_URL from '../../../../Config/api';

// Columns the /api/upload-excel handler reads from the first sheet.
const EXPECTED_COLUMNS = [
    ['Title', 'Product name'],
    ['Colour', 'Colour variant'],
    ['Fabric', 'Fabric variant'],
    ['SKU', 'Required · must be unique'],
    ['Size', 'Comma-separated, e.g. "SM, ML"'],
    ['HSN Code', 'Tax HSN code'],
    ['Collection', "e.g. SS'27 or CORE COLLECTION"],
    ['Wholesale Price', 'EUR wholesale'],
    ['Retail Euro', 'EUR retail'],
];

const UploadHSN = () => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState(null); // { type: 'success'|'danger', message, duplicates }
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);

    const selectFile = (f) => {
        if (!f) return;
        setFile(f);
        setStatus(null);
    };

    const handleFileChange = (e) => {
        selectFile(e.target.files[0]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        selectFile(e.dataTransfer.files[0]);
    };

    const clearFile = () => {
        setFile(null);
        setStatus(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleFileUpload = async () => {
        if (!file) {
            setStatus({ type: 'danger', message: 'Please select a file first.' });
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            setUploading(true);
            setStatus(null);
            const response = await axios.post(`${API_BASE_URL}/api/upload-excel`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            const warnings = response.data.warnings || [];
            setStatus({
                type: warnings.length > 0 ? 'warning' : 'success',
                message: response.data.message || 'Upload complete.',
                inserted: response.data.inserted || [],
                updated: response.data.updated || [],
                duplicates: response.data.duplicates || [],
                warnings,
            });
        } catch (error) {
            console.error('Error uploading file:', error.response?.data || error.message);
            setStatus({
                type: 'danger',
                message: error.response?.data?.error || 'Upload failed. Please check the file and try again.',
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Upload HSN" parent="Products" title="Upload HSN" />
            <Container fluid={true}>
                <Row>
                    <Col xl="8" lg="10" className="mx-auto">
                        <Card>
                            <CardHeader>
                                <h4>Import Product Data</h4>
                                <span>Upload an Excel (.xlsx / .xls) file to add products in bulk.</span>
                            </CardHeader>
                            <CardBody>
                                <div
                                    className={`rf-dropzone ${dragging ? 'is-dragging' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => inputRef.current && inputRef.current.click()}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current && inputRef.current.click(); } }}
                                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                    onDragLeave={() => setDragging(false)}
                                    onDrop={handleDrop}
                                >
                                    <div className="rf-dropzone-icon"><UploadCloud /></div>
                                    <h6>Drag &amp; drop your file here, or <span className="rf-browse">browse</span></h6>
                                    <small>Accepted formats: .xlsx, .xls</small>
                                </div>
                                <input
                                    ref={inputRef}
                                    type="file"
                                    className="d-none"
                                    onChange={handleFileChange}
                                    accept=".xlsx, .xls"
                                />

                                {file && (
                                    <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
                                        <span className="rf-file-chip">
                                            <FileText size={16} />
                                            {file.name}
                                            <span className="rf-file-remove" role="button" aria-label="Remove file" onClick={clearFile}>
                                                <X size={15} />
                                            </span>
                                        </span>
                                    </div>
                                )}

                                {status && (
                                    <Alert color={status.type} className="mt-3 mb-0">
                                        {status.message}
                                        {status.inserted && status.inserted.length > 0 && (
                                            <div className="mt-1">
                                                <strong>{status.inserted.length}</strong> new product(s) added: {status.inserted.join(', ')}
                                            </div>
                                        )}
                                        {status.updated && status.updated.length > 0 && (
                                            <div className="mt-1">
                                                <strong>{status.updated.length}</strong> existing product(s) updated: {status.updated.join(', ')}
                                            </div>
                                        )}
                                        {status.duplicates && status.duplicates.length > 0 && (
                                            <div className="mt-1">
                                                <strong>{status.duplicates.length}</strong> row(s) matched an existing SKU: {status.duplicates.join(', ')}
                                            </div>
                                        )}
                                        {status.warnings && status.warnings.length > 0 && (
                                            <div className="mt-2">
                                                <strong>{status.warnings.length}</strong> row(s) had issues that need a look:
                                                <ul className="mb-0 ps-3">
                                                    {status.warnings.map((w, i) => (
                                                        <li key={i}>
                                                            Row {w.row}{w.sku ? ` (SKU ${w.sku})` : ''}: {w.issues.join('; ')}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </Alert>
                                )}

                                <div className="d-flex align-items-center gap-2 mt-4">
                                    <Button color="primary" onClick={handleFileUpload} disabled={!file || uploading}>
                                        {uploading ? 'Uploading…' : 'Upload File'}
                                    </Button>
                                    {file && !uploading && (
                                        <Button color="link" onClick={clearFile}>Clear</Button>
                                    )}
                                </div>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader>
                                <h4>Expected columns</h4>
                                <span>Your file's first sheet should include these headers.</span>
                            </CardHeader>
                            <CardBody className="p-0">
                                <Table className="mb-0" responsive>
                                    <thead>
                                        <tr>
                                            <th>Column</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {EXPECTED_COLUMNS.map(([col, note]) => (
                                            <tr key={col}>
                                                <td><code>{col}</code></td>
                                                <td className="text-muted">{note}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default UploadHSN;
